// Panel de gastos en modo solo lectura para superadmin.
// Versión reducida de Contabilidad.js: sin acciones de adelanto, devolución,
// liquidar, generar APU, ni edición/eliminación de gastos o préstamos.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { confirmAndLogout } from '../../../utils/session';
import { useAuth } from '../../../context/authContext';
import AuthImage from "../../../components/AuthImage";

const formatFecha = (s) => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d)) return s;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatFechaHora = (s) => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d)) return s;
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const SUP_COLORS = [
  { border: '#3b82f6', headerBg: 'linear-gradient(100deg, #eff6ff 0%, #f8fbff 60%, #fff 100%)', accent: '#1d4ed8', lightBg: '#eff6ff', tag: '#dbeafe' },
  { border: '#10b981', headerBg: 'linear-gradient(100deg, #ecfdf5 0%, #f5fdf9 60%, #fff 100%)', accent: '#047857', lightBg: '#ecfdf5', tag: '#d1fae5' },
  { border: '#8b5cf6', headerBg: 'linear-gradient(100deg, #f5f3ff 0%, #faf8ff 60%, #fff 100%)', accent: '#6d28d9', lightBg: '#f5f3ff', tag: '#ede9fe' },
  { border: '#f59e0b', headerBg: 'linear-gradient(100deg, #fffbeb 0%, #fdfaf0 60%, #fff 100%)', accent: '#b45309', lightBg: '#fffbeb', tag: '#fef3c7' },
  { border: '#ef4444', headerBg: 'linear-gradient(100deg, #fef2f2 0%, #fdf7f7 60%, #fff 100%)', accent: '#b91c1c', lightBg: '#fef2f2', tag: '#fee2e2' },
  { border: '#06b6d4', headerBg: 'linear-gradient(100deg, #ecfeff 0%, #f5fdff 60%, #fff 100%)', accent: '#0e7490', lightBg: '#ecfeff', tag: '#cffafe' },
  { border: '#ec4899', headerBg: 'linear-gradient(100deg, #fdf2f8 0%, #fef7fb 60%, #fff 100%)', accent: '#be185d', lightBg: '#fdf2f8', tag: '#fce7f3' },
  { border: '#84cc16', headerBg: 'linear-gradient(100deg, #f7fee7 0%, #fafdf0 60%, #fff 100%)', accent: '#4d7c0f', lightBg: '#f7fee7', tag: '#ecfccb' },
];

const GastosReadOnly = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const rolActual = authUser?.tipo || '';
  const panelOrigen = (rolActual === 'superadmin')
    ? '/superadmin/compras'
    : '/admin';
  const rolHeader = { 'X-Rol': 'superadmin' };

  const [supervisoras, setSupervisoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fotoAmpliada, setFotoAmpliada] = useState(null);
  const [expandedSups, setExpandedSups] = useState({});
  const [expandedBolsas, setExpandedBolsas] = useState({});

  useEffect(() => { window.scrollTo(0, 0); fetchResumen(); }, []);

  const fetchResumen = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get('/api/prestamos/prestamos/resumen', { headers: rolHeader });
      setSupervisoras(r.data.supervisoras || []);
    } catch (e) {
      setError('Error cargando datos: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  const toggleSup = (user) => setExpandedSups((p) => ({ ...p, [user]: !p[user] }));
  const toggleBolsa = (key) => setExpandedBolsas((p) => ({ ...p, [key]: !p[key] }));
  const handleLogout = () => confirmAndLogout(navigate, '/login');

  return (
    <div style={st.page}>
      <div style={st.header}>
        <h2 style={st.titulo}>Panel Gastos (solo lectura)</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => navigate(panelOrigen)} style={st.btnVolver}>← Volver</button>
          <button onClick={handleLogout} style={st.btnLogout}>Cerrar sesión</button>
        </div>
      </div>

      {loading && <p style={st.msg}>Cargando...</p>}
      {error && <p style={{ ...st.msg, color: '#ef4444' }}>{error}</p>}
      {!loading && !error && supervisoras.length === 0 && <p style={st.msg}>No hay datos aún.</p>}

      {supervisoras.map((sup, idx) => {
        const isExpanded = expandedSups[sup.user_supervisora];
        const saldoColor = sup.saldo >= 0 ? '#059669' : '#ef4444';
        const saldoBg = sup.saldo >= 0 ? '#ecfdf5' : '#fef2f2';
        const pal = SUP_COLORS[idx % SUP_COLORS.length];

        return (
          <div key={sup.user_supervisora} style={{ ...st.supCard, borderLeft: `4px solid ${pal.border}` }}>
            <div style={{ ...st.supHeader, background: pal.headerBg }} onClick={() => toggleSup(sup.user_supervisora)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: pal.border, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 16, flexShrink: 0 }}>
                  {sup.user_supervisora.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ ...st.supNombre, color: pal.accent }}>{sup.user_supervisora}</div>
                  <div style={st.supSubtitle}>
                    Prestado: <strong>{sup.total_prestado.toFixed(2)}€</strong>
                    {' · '}Gastado (liq.): <strong>{sup.total_gastado_liquidado.toFixed(2)}€</strong>
                    {' · '}Pendiente: <strong>{sup.total_pendiente.toFixed(2)}€</strong>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <div style={{ background: saldoBg, color: saldoColor, borderRadius: 10, padding: '4px 12px', fontWeight: 800, fontSize: 16, border: `1px solid ${sup.saldo >= 0 ? '#a7f3d0' : '#fecaca'}` }}>
                  {sup.saldo >= 0 ? '+' : ''}{sup.saldo.toFixed(2)} €
                </div>
                <div style={st.chevron}>{isExpanded ? '▲' : '▼'}</div>
              </div>
            </div>

            {isExpanded && (
              <div style={st.supBody}>
                {/* Adelantos */}
                {(() => {
                  const prestamosPos = sup.prestamos.filter((p) => parseFloat(p.importe) > 0);
                  return (
                    <div style={st.seccion}>
                      <div style={{ ...st.seccionTitulo, color: pal.accent, borderBottom: `2px solid ${pal.tag}`, paddingBottom: 4 }}>Adelantos</div>
                      {prestamosPos.length === 0 ? (
                        <p style={st.empty}>Sin adelantos.</p>
                      ) : (
                        prestamosPos.map((p) => (
                          <div key={p.id} style={st.prestamoRow}>
                            <div>
                              <span style={st.prestamoImporte}>{parseFloat(p.importe).toFixed(2)} €</span>
                              <span style={st.prestamoFecha}> · {formatFechaHora(p.fecha)}</span>
                              {p.notas && <span style={st.prestamoNotas}> · {p.notas}</span>}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  );
                })()}

                {/* Devoluciones */}
                {(() => {
                  const devoluciones = sup.prestamos.filter((p) => parseFloat(p.importe) < 0);
                  return (
                    <div style={st.seccion}>
                      <div style={{ ...st.seccionTitulo, color: '#d97706', borderBottom: '2px solid #fef3c7', paddingBottom: 4 }}>Devoluciones</div>
                      {devoluciones.length === 0 ? (
                        <p style={st.empty}>Sin devoluciones.</p>
                      ) : (
                        devoluciones.map((p) => (
                          <div key={p.id} style={st.prestamoRow}>
                            <div>
                              <span style={{ ...st.prestamoImporte, color: '#ef4444' }}>{parseFloat(p.importe).toFixed(2)} €</span>
                              <span style={st.prestamoFecha}> · {formatFechaHora(p.fecha)}</span>
                              {p.notas && <span style={st.prestamoNotas}> · {p.notas}</span>}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  );
                })()}

                {/* Bolsas */}
                <div style={st.seccion}>
                  <div style={{ ...st.seccionTitulo, color: pal.accent, borderBottom: `2px solid ${pal.tag}`, paddingBottom: 4 }}>Bolsas de gastos</div>
                  {sup.bolsas.length === 0 ? (
                    <p style={st.empty}>Sin gastos registrados.</p>
                  ) : (
                    sup.bolsas.map((bolsa) => {
                      const bolsaKey = `${sup.user_supervisora}_${bolsa.n_bolsa}`;
                      const bolsaExp = expandedBolsas[bolsaKey];
                      return (
                        <div key={bolsa.n_bolsa} style={{ ...st.bolsaCard, borderLeft: `3px solid ${pal.border}` }}>
                          <div style={{ ...st.bolsaCab, background: pal.lightBg }} onClick={() => toggleBolsa(bolsaKey)}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ ...st.bolsaN, color: pal.accent }}>Bolsa #{bolsa.n_bolsa}</span>
                                <span style={{ ...st.badge, background: bolsa.estado === 'liquidada' ? '#10b981' : '#f59e0b' }}>
                                  {bolsa.estado === 'liquidada' ? 'Liquidada' : 'Activa'}
                                </span>
                              </div>
                              {bolsa.estado === 'liquidada' && bolsa.fecha_liquidacion && (
                                <span style={{ fontSize: 11, color: '#6b7280' }}>
                                  Liquidada el {formatFecha(bolsa.fecha_liquidacion)}
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <span style={st.bolsaTotal}>{bolsa.total.toFixed(2)} €</span>
                              <span style={{ fontSize: 10, color: '#9ca3af' }}>{bolsaExp ? '▲' : '▼'}</span>
                            </div>
                          </div>

                          {bolsaExp && (
                            <div style={st.bolsaDetalle}>
                              {bolsa.gastos.map((g) => (
                                <GastoReadOnly key={g.id} gasto={g} onVerFoto={setFotoAmpliada} />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {fotoAmpliada && (
        <div style={st.overlay} onClick={() => setFotoAmpliada(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, maxWidth: '95vw' }}>
            <AuthImage src={fotoAmpliada} alt="Ticket" style={{ maxWidth: '95vw', maxHeight: '80vh', borderRadius: 10, objectFit: 'contain' }} />
            <button onClick={() => setFotoAmpliada(null)} style={st.btnCancelar}>Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
};

const TIPO_LABEL = { piso: 'Piso', reuniones: 'Reuniones' };

const CAT_COLORS = {
  menaje:       { bg: '#eff6ff', border: '#3b82f6', text: '#1d4ed8' },
  comida:       { bg: '#fff7ed', border: '#f97316', text: '#c2410c' },
  limpieza:     { bg: '#ecfdf5', border: '#10b981', text: '#065f46' },
  varios:       { bg: '#f5f3ff', border: '#8b5cf6', text: '#5b21b6' },
  aparcamiento: { bg: '#fffbeb', border: '#f59e0b', text: '#92400e' },
};

const TIPO_COLORS = {
  piso:      { bg: '#eef2ff', border: '#6366f1', text: '#3730a3' },
  reuniones: { bg: '#fdf2f8', border: '#ec4899', text: '#9d174d' },
};

const GastoReadOnly = ({ gasto, onVerFoto }) => {
  const fmtFechaHora = (s) => {
    if (!s) return '';
    const d = new Date(s.replace(' ', 'T'));
    if (isNaN(d)) return s;
    return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };
  const fmtFecha = (s) => {
    if (!s) return '';
    const d = new Date(s);
    if (isNaN(d)) return s;
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const cat = CAT_COLORS[gasto.categoria] || CAT_COLORS.varios;
  return (
    <div style={{ ...gc.row, borderLeft: `4px solid ${cat.border}` }}>
      <div style={gc.left}>
        <span style={{ ...gc.dot, background: gasto.estado === 'liquidado' ? '#10b981' : '#f59e0b' }} />
        <div>
          <div style={{ ...gc.titulo, color: gasto.categoria === 'aparcamiento' ? CAT_COLORS.aparcamiento.text : ((TIPO_COLORS[gasto.tipo_lugar] || {}).text || '#111827') }}>
            #{gasto.id} · {gasto.categoria === 'aparcamiento' ? 'Aparcamiento' : (TIPO_LABEL[gasto.tipo_lugar] || gasto.tipo_lugar || '')}{gasto.abreviatura_piso ? ` (${gasto.abreviatura_piso})` : ''}
          </div>
          <div style={gc.sub}>
            <span style={{ display: 'inline-block', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700, background: cat.bg, color: cat.text, border: `1px solid ${cat.border}` }}>{gasto.categoria}</span>
            {' · Fecha gasto: '}{fmtFecha(gasto.fecha_gasto)}
          </div>
          <div style={gc.sub}>Añadido: {fmtFechaHora(gasto.fecha_creacion)}</div>
          {gasto.notas && <div style={gc.sub}>Notas: {gasto.notas}</div>}
          {gasto.foto_path && (
            <AuthImage
              src={`/api/tickets_gastos/${encodeURIComponent(gasto.foto_path)}`}
              alt="Ticket"
              style={{ maxWidth: 120, maxHeight: 80, borderRadius: 6, objectFit: 'contain', cursor: 'pointer', marginTop: 4, border: '1px solid #e5e7eb' }}
              onClick={() => onVerFoto(`/api/tickets_gastos/${encodeURIComponent(gasto.foto_path)}`)}
            />
          )}
        </div>
      </div>
      <div style={gc.right}>
        <span style={gc.importe}>{parseFloat(gasto.importe).toFixed(2)} €</span>
      </div>
    </div>
  );
};

const st = {
  page: { maxWidth: 700, margin: '0 auto', padding: '0 0 80px', fontFamily: "'Segoe UI', sans-serif", background: 'linear-gradient(160deg, #f0f4ff 0%, #f9fafb 50%, #f0fdf4 100%)', minHeight: '100vh' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px 12px', background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 40, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', flexWrap: 'wrap', gap: 8 },
  titulo: { margin: 0, fontSize: 18, fontWeight: 800, color: '#1e3a8a' },
  btnVolver: { background: '#6b7280', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  btnLogout: { background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  msg: { textAlign: 'center', color: '#6b7280', marginTop: 40 },
  supCard: { margin: '14px 12px 0', background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 4px 18px rgba(0,0,0,0.07)' },
  supHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 16px', cursor: 'pointer', userSelect: 'none' },
  supNombre: { fontWeight: 800, fontSize: 16, color: '#111827' },
  supSubtitle: { fontSize: 12, color: '#6b7280', marginTop: 3 },
  chevron: { fontSize: 11, color: '#9ca3af', textAlign: 'right', marginTop: 2 },
  supBody: { borderTop: '1px solid #f3f4f6', padding: '14px 16px' },
  seccion: { marginBottom: 16 },
  seccionTitulo: { fontWeight: 700, fontSize: 12, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 },
  empty: { fontSize: 13, color: '#9ca3af', margin: 0 },
  prestamoRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f3f4f6' },
  prestamoImporte: { fontWeight: 700, fontSize: 14, color: '#10b981' },
  prestamoFecha: { fontSize: 12, color: '#6b7280' },
  prestamoNotas: { fontSize: 12, color: '#6b7280' },
  bolsaCard: { border: '1px solid #e5e7eb', borderRadius: 12, marginBottom: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  bolsaCab: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', cursor: 'pointer' },
  bolsaN: { fontWeight: 700, fontSize: 14 },
  badge: { color: '#fff', borderRadius: 20, padding: '3px 11px', fontSize: 11, fontWeight: 700 },
  bolsaTotal: { fontWeight: 800, fontSize: 14, color: '#ef4444' },
  bolsaDetalle: { background: '#fafafa' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  btnCancelar: { background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: '11px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
};

const gc = {
  row: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid #f3f4f6' },
  left: { display: 'flex', gap: 8, flex: 1 },
  dot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0, marginTop: 5 },
  titulo: { fontWeight: 600, fontSize: 13, color: '#111827' },
  sub: { fontSize: 12, color: '#6b7280', marginTop: 1 },
  right: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, marginLeft: 8 },
  importe: { fontWeight: 700, fontSize: 14, color: '#ef4444' },
};

export default GastosReadOnly;
